import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import * as crypto from 'node:crypto';
import { runClassMetadataAgent } from '../api/agents/extraction-modules/class-metadata.js';
import { ClassRoom } from '../classroom.js';
import { Exam } from '../exam.js';
import { normalizeSubject } from '../subjects.js';
import { Teacher } from '../teacher.js';
import { generateJoinCode, isDuplicateKeyError } from './join-code.js';

const MAX_CODE_GENERATION_ATTEMPTS = 10;

/** Course-type markers that read as a prefix ("LK 12") rather than a suffix letter ("9a"). */
const COURSE_SUFFIX_RE = /^(LK|GK|eA|gA)$/i;

/**
 * Splits a combined class label into its grade and sub-class ("suffix") components, e.g.
 * "9a" -> {grade: "9", suffix: "a"}, "LK 12" / "12 LK" -> {grade: "12", suffix: "LK"}, "Q1" -> {grade: "Q1"}.
 * Best-effort: an unrecognized shape is returned whole as `grade` with no `suffix`.
 */
export function parseClassName(raw: string): { grade: string; suffix?: string } {
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  if (!trimmed) return { grade: '' };

  let m = trimmed.match(/^(LK|GK|eA|gA)\s*(.+)$/i);
  if (m) return { grade: m[2]!.trim(), suffix: m[1]!.toUpperCase() };

  m = trimmed.match(/^(.+?)\s+(LK|GK|eA|gA)$/i);
  if (m) return { grade: m[1]!.trim(), suffix: m[2]!.toUpperCase() };

  m = trimmed.match(/^(\d+)\s*([a-zA-Z])$/);
  if (m) return { grade: m[1]!, suffix: m[2]!.toLowerCase() };

  return { grade: trimmed };
}

/**
 * Joins a grade and an optional suffix back into the canonical combined class label — the inverse of
 * {@link parseClassName}. Letter suffixes attach directly ("9" + "a" -> "9a"), course-type suffixes get
 * a leading, spaced, uppercased prefix ("12" + "LK" -> "LK 12"); anything else falls back to "grade suffix".
 */
export function buildClassName(grade: string, suffix?: string | null): string {
  const g = typeof grade === 'string' ? grade.trim() : '';
  const s = typeof suffix === 'string' ? suffix.trim() : '';
  if (!g) return s;
  if (!s) return g;
  if (COURSE_SUFFIX_RE.test(s)) return `${s.toUpperCase()} ${g}`;
  if (/^[a-zA-Z]$/.test(s)) return `${g}${s.toLowerCase()}`;
  return `${g} ${s}`;
}

/**
 * Creates a new classroom owned by `teacherID`. Shared by the `createClassroomMutation` (manual
 * creation from the sidebar) and `linkExamToClassroom` (auto-creation while tagging an exam with a
 * class that doesn't exist yet as a managed classroom).
 *
 * `subject` is required — a class is scoped by subject (see ClassRoom.subject) — and is normalized
 * to a canonical {@link Subject} key so it always matches what's stored on `Exam.subject`. It is
 * ALWAYS taken from this argument, never re-derived from `name` — the manual creation dialog already
 * collects it as its own required dropdown, a more explicit signal than anything parsed out of the
 * free-text name.
 *
 * `grade`/`suffix` are stored alongside `name` for structured display/matching; when not passed
 * explicitly they are derived from `name` via {@link runClassMetadataAgent} — the SAME normalization
 * `linkExamToClassroom` applies, so the manual dialog's single free-text name field (which just as
 * easily ends up containing a combined label like "LK 12 Sozialkunde" as anything typed in chat) gets
 * the same quality of grade/suffix extraction, not the older plain-regex fallback.
 */
export async function createClassroomForTeacher(
  teacherID: string,
  name: string,
  subject: string,
  extra?: { grade?: string; suffix?: string },
): Promise<ClassRoom> {
  const trimmedName = typeof name === 'string' ? name.trim() : '';
  if (!trimmedName) {
    throw new Error('Klassenname darf nicht leer sein');
  }
  const normalizedSubject = normalizeSubject(subject);

  let grade = extra?.grade?.trim() || undefined;
  let suffix = extra?.suffix?.trim() || undefined;
  if (!grade) {
    const extracted = await runClassMetadataAgent(trimmedName);
    grade = extracted.grade ?? undefined;
    suffix = suffix ?? extracted.suffix ?? undefined;
    if (!grade) {
      const fallback = parseClassName(trimmedName);
      grade = fallback.grade || undefined;
      suffix = suffix ?? fallback.suffix;
    }
  }

  const classroomCollection = await getCollection(ClassRoom);

  // Relies on the unique index on `code` (see ensure-classroom-indexes.ts) to make this race-free:
  // instead of checking-then-inserting, we insert straight away and only retry on an actual collision.
  for (let attempt = 0; attempt < MAX_CODE_GENERATION_ATTEMPTS; attempt++) {
    const candidate = new ClassRoom({
      _id: crypto.randomUUID(),
      teacherID,
      name: trimmedName,
      subject: normalizedSubject,
      grade,
      suffix,
      code: generateJoinCode(),
      createdAt: new Date().toISOString(),
    });
    try {
      await classroomCollection.insertOne(candidate.toObject());
      return candidate;
    } catch (err) {
      if (!isDuplicateKeyError(err)) throw err;
    }
  }

  throw new Error('Failed to generate a unique classroom code after 10 attempts');
}

/**
 * Case-/whitespace-insensitive lookup of one of `teacherID`'s classrooms by display name AND
 * subject — the same name in a different subject (e.g. "9a" in math vs. "9a" in physics) is a
 * different classroom, since each has its own roster/join code.
 */
export async function findTeacherClassroomByNameAndSubject(
  teacherID: string,
  name: string,
  subject: string,
): Promise<ClassRoom | null> {
  const trimmedName = typeof name === 'string' ? name.trim() : '';
  if (!trimmedName) return null;
  const normalizedSubject = normalizeSubject(subject);

  const classroomCollection = await getCollection(ClassRoom);
  const classrooms = await classroomCollection.find({ teacherID }).toArray();
  const match = classrooms.find(
    c =>
      String((c as any)?.name ?? '').trim().toLowerCase() === trimmedName.toLowerCase() &&
      normalizeSubject((c as any)?.subject) === normalizedSubject,
  );
  return (match as ClassRoom) ?? null;
}

export interface ClassroomLinkResult {
  /** The class name to store on `Exam.class` — the matched (or newly created) classroom's canonical
   * name, or the class name as typed if no teacher profile could be resolved at all. */
  resolvedClass: string;
  /** Canonical {@link Subject} key actually used to match/create the classroom. Equals the `subject`
   * argument unless `subjectFromClassLabel` is true. */
  resolvedSubject: string;
  /** True when `resolvedSubject` was parsed OUT OF `klasse` itself (e.g. "LK 12 Sozialkunde") rather
   * than taken from the `subject` argument. Callers that treat `subject` as merely a fallback hint (not
   * yet a confirmed value) should write `resolvedSubject` back onto `Exam.subject` when this is true. */
  subjectFromClassLabel: boolean;
  linked: 'matched' | 'created' | 'unresolved';
  /** Join code of a classroom created by this call (only set when `linked === 'created'`), so the
   * caller can tell the teacher how students join it. */
  code?: string;
  /** `_id` of the matched/created `ClassRoom` — set whenever `linked !== 'unresolved'`. Callers should
   * write this onto `Exam.classRoomID` alongside `resolvedClass`, so the exam is joined to its class by
   * a real foreign key rather than only by the (name, subject) string pair. */
  classRoomID?: string;
}

/**
 * Links an exam to one of its teacher's existing classrooms for the given subject
 * (case-/whitespace-insensitive name match), creating that classroom on the spot when none matches
 * yet — an exam's class/subject being set must always be enough for it to show up under a real,
 * visible class, without waiting on a separate confirmation step the teacher might miss. Shared by
 * every path that learns an exam's class — the `set_metadata` tool (both the initial fach/thema call
 * and the follow-up `klasse` correction) and the creation flow's `create_exam` tool — so an exam ends
 * up tied to its class the same way
 * regardless of whether the class was typed while creating the exam or while grading it.
 *
 * `klasse` is normalized through {@link runClassMetadataAgent} FIRST: teachers (and the main agent
 * composing tool arguments on their behalf) routinely combine grade, suffix AND subject into one string
 * ("LK 12 Sozialkunde", "8c Mathe") — the plain regex parser (`parseClassName`) cannot reliably tell
 * those apart and lets the subject word leak into the stored class name/grade. The agent call falls
 * back to `parseClassName` when it errors or recognizes no grade at all, so a classroom is always
 * matched/created even if the LLM call fails.
 *
 * The returned `classRoomID` (whenever `linked !== 'unresolved'`) is what every caller writes onto
 * `Exam.classRoomID` — a real foreign key to the `ClassRoom`, alongside the display-only `class`/
 * `subject` strings. `get-class-exam-overview.ts` (the teacher's class section) matches on it directly
 * instead of only on the (name, subject) string pair, which silently breaks if either one drifts from
 * the classroom's own values (e.g. after a rename) or if two different pieces of raw text happen to
 * normalize to the same display string for different classrooms.
 */
export async function linkExamToClassroom(examId: string, klasse: string, subject: string): Promise<ClassroomLinkResult> {
  const extracted = await runClassMetadataAgent(klasse);
  let grade = extracted.grade;
  let suffix = extracted.suffix ?? undefined;
  if (!grade) {
    const fallback = parseClassName(klasse);
    grade = fallback.grade || klasse;
    suffix = suffix ?? fallback.suffix;
  }
  const normalizedKlasse = buildClassName(grade, suffix) || klasse;
  const subjectFromClassLabel = extracted.subject !== null;
  const resolvedSubject = normalizeSubject(subjectFromClassLabel ? extracted.subject : subject);

  let resolvedClass = normalizedKlasse;
  let linked: ClassroomLinkResult['linked'] = 'unresolved';
  let code: string | undefined;
  let classRoomID: string | undefined;
  try {
    const examCollection = await getCollection(Exam as any);
    const examDoc = await examCollection.findOne({ _id: examId } as any, { projection: { userID: 1 } } as any);
    const userID = (examDoc as any)?.userID;
    const teacher = userID ? await (await getCollection(Teacher)).findOne({ userID }) : null;
    if (teacher) {
      const matchedClassroom = await findTeacherClassroomByNameAndSubject(teacher._id, normalizedKlasse, resolvedSubject);
      if (matchedClassroom) {
        resolvedClass = matchedClassroom.name;
        classRoomID = matchedClassroom._id;
        linked = 'matched';
      } else {
        const classroom = await createClassroomForTeacher(teacher._id, normalizedKlasse, resolvedSubject, {
          grade,
          suffix,
        });
        resolvedClass = classroom.name;
        code = classroom.code;
        classRoomID = classroom._id;
        linked = 'created';
      }
    } else {
      logger.warn(
        { examId, userID, context: 'LinkExamToClassroom' },
        'No teacher profile found for exam owner — class was not linked to any managed classroom',
      );
    }
  } catch (err) {
    logger.warn({ err, examId, context: 'LinkExamToClassroom' }, 'Failed to resolve classroom link for class');
  }
  return { resolvedClass, resolvedSubject, subjectFromClassLabel, linked, code, classRoomID };
}
